import { useEffect, useState } from "react";

export interface ImageInfo {
  width: number;
  height: number;
  size: string | null;
  bytes: number | null;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function useImageInfo(url: string | null | undefined): ImageInfo | null {
  const [info, setInfo] = useState<ImageInfo | null>(null);

  useEffect(() => {
    setInfo(null);
    if (!url) return;

    let cancelled = false;

    // Load image first to get dimensions immediately.
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const { naturalWidth: width, naturalHeight: height } = img;
      
      // Initial state with dimensions
      setInfo({ width, height, size: null, bytes: null });

      // Fetch for file size information. Use no-cache to ensure content-length is returned.
      fetch(url, { cache: "no-cache" })
        .then((res) => {
          if (!res.ok) throw new Error("Fetch failed");
          const len = res.headers.get("content-length");
          if (len) return parseInt(len, 10);
          return res.blob().then((b) => b.size);
        })
        .then((bytes) => {
          if (!cancelled && bytes !== null)
            setInfo({ width, height, size: formatBytes(bytes), bytes });
        })
        .catch((err) => {
          console.warn(`Could not fetch image size for ${url}:`, err);
        });
    };
    img.onerror = () => {
      console.warn(`Failed to load image: ${url}`);
    };
    img.src = url;

    return () => {
      cancelled = true;
    };
  }, [url]);

  return info;
}

export function formatImageInfo(info: ImageInfo): string {
  const parts = [`${info.width} × ${info.height}px`];
  
  if (info.size) {
    parts.push(info.size);
  }
  
  return parts.join(" · ");
}
